// Shared UI primitives
import { ReactNode, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../lib/i18n';
import { Icon } from './Icon';

// ---------- LOADER ----------
export function Loader({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sz = size === 'sm' ? 16 : size === 'lg' ? 40 : 24;
  return <span className="spinner" style={{ width: sz, height: sz }} />;
}

// ---------- BUTTON ----------
type BtnProps = {
  children?: ReactNode;
  variant?: 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  icon?: ReactNode;
  iconRight?: ReactNode;
  as?: 'button' | 'a';
  to?: string;
  href?: string;
  title?: string;
  ariaLabel?: string;
};
export function Button({ children, variant = 'primary', size = 'md', block, loading, disabled, type = 'button', onClick, className = '', icon, iconRight, as = 'button', to, href, title, ariaLabel }: BtnProps) {
  const cls = `btn btn-${variant} ${size !== 'md' ? `btn-${size}` : ''} ${block ? 'btn-block' : ''} ${className}`;
  if (as === 'a' && (to || href)) {
    return <a href={to || href} className={cls} title={title} aria-label={ariaLabel}>{icon}{children}{iconRight}</a>;
  }
  return (
    <button type={type} className={cls} onClick={onClick} disabled={disabled || loading} title={title} aria-label={ariaLabel}>
      {loading ? <span className="spinner" style={{ width: 16, height: 16 }} /> : icon}
      {children}
      {!loading && iconRight}
    </button>
  );
}

// ---------- INPUT ----------
type InpProps = {
  label?: string;
  hint?: string;
  error?: string;
  type?: string;
  value?: any;
  defaultValue?: any;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  autoComplete?: string;
  min?: any;
  max?: any;
  size?: 'sm' | 'md' | 'lg';
  icon?: ReactNode;
  className?: string;
};
export function Input({ label, hint, error, type = 'text', value, defaultValue, placeholder, onChange, required, disabled, name, autoComplete, min, max, size = 'md', icon, className = '' }: InpProps) {
  const inputId = useId('inp');
  return (
    <div className={`field ${className}`}>
      {label && <label className="label" htmlFor={inputId}>{label}{required && <span style={{ color: 'var(--color-danger)' }}> *</span>}</label>}
      {icon ? (
        <div className="input-group">
          <span className="input-group-icon">{icon}</span>
          <input id={inputId} type={type} className={`input ${size !== 'md' ? `input-${size}` : ''} ${error ? 'input-error' : ''}`} value={value} defaultValue={defaultValue} placeholder={placeholder} onChange={onChange} required={required} disabled={disabled} name={name} autoComplete={autoComplete} min={min} max={max} />
        </div>
      ) : (
        <input id={inputId} type={type} className={`input ${size !== 'md' ? `input-${size}` : ''} ${error ? 'input-error' : ''}`} value={value} defaultValue={defaultValue} placeholder={placeholder} onChange={onChange} required={required} disabled={disabled} name={name} autoComplete={autoComplete} min={min} max={max} />
      )}
      {error ? <p className="field-error">{error}</p> : hint ? <p className="field-hint">{hint}</p> : null}
    </div>
  );
}

// ---------- SELECT ----------
type SelProps = {
  label?: string;
  value?: any;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: any; label: string }[];
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  hint?: string;
};
export function Select({ label, value, onChange, options, required, disabled, placeholder, className = '', hint }: SelProps) {
  const id = useId('sel');
  return (
    <div className={`field ${className}`}>
      {label && <label className="label" htmlFor={id}>{label}{required && <span style={{ color: 'var(--color-danger)' }}> *</span>}</label>}
      <select id={id} className="select" value={value || ''} onChange={onChange} required={required} disabled={disabled}>
        {placeholder && <option value="" disabled>{placeholder}</option>}
        {options.map(o => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
      </select>
      {hint && <p className="field-hint">{hint}</p>}
    </div>
  );
}

// ---------- TEXTAREA ----------
type TAProps = {
  label?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  rows?: number;
  required?: boolean;
  hint?: string;
};
export function Textarea({ label, value, onChange, placeholder, rows = 4, required, hint }: TAProps) {
  const id = useId('ta');
  return (
    <div className="field">
      {label && <label className="label" htmlFor={id}>{label}{required && <span style={{ color: 'var(--color-danger)' }}> *</span>}</label>}
      <textarea id={id} className="textarea" value={value} onChange={onChange} placeholder={placeholder} rows={rows} required={required} />
      {hint && <p className="field-hint">{hint}</p>}
    </div>
  );
}

// ---------- MODAL ----------
type ModalProps = { open: boolean; onClose: () => void; title?: string; children: ReactNode; footer?: ReactNode; size?: 'sm' | 'md' | 'lg' };
export function Modal({ open, onClose, title, children, footer, size = 'md' }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, onClose]);
  if (!open) return null;
  const widths = { sm: 380, md: 560, lg: 800 };
  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal" style={{ maxWidth: widths[size] }} onClick={e => e.stopPropagation()}>
        {title && <div className="modal-header"><h3 style={{ margin: 0, fontSize: 'var(--fs-lg)' }}>{title}</h3><button className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label="Close"><Icon.X size={18} /></button></div>}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ---------- BADGE ----------
export function Badge({ children, variant = 'default', className = '' }: { children: ReactNode; variant?: string; className?: string }) {
  return <span className={`badge ${variant !== 'default' ? `badge-${variant}` : ''} ${className}`}>{children}</span>;
}

// ---------- STATUS BADGE ----------
const statusMap: Record<string, { en: string; ar: string }> = {
  draft: { en: 'Draft', ar: 'مسودة' },
  pending_availability: { en: 'Checking', ar: 'جاري التحقق' },
  quoted: { en: 'Quoted', ar: 'عرض سعر' },
  awaiting_payment: { en: 'Awaiting payment', ar: 'بانتظار الدفع' },
  payment_processing: { en: 'Processing', ar: 'قيد المعالجة' },
  awaiting_supplier: { en: 'Awaiting travel company', ar: 'بانتظار الشركة السياحية' },
  confirmed: { en: 'Confirmed', ar: 'مؤكد' },
  partially_confirmed: { en: 'Partially confirmed', ar: 'مؤكد جزئياً' },
  in_progress: { en: 'In progress', ar: 'قيد التنفيذ' },
  completed: { en: 'Completed', ar: 'مكتمل' },
  cancellation_requested: { en: 'Cancellation requested', ar: 'طلب إلغاء' },
  cancelled: { en: 'Cancelled', ar: 'ملغي' },
  refund_pending: { en: 'Refund pending', ar: 'استرداد معلق' },
  refunded: { en: 'Refunded', ar: 'مسترد' },
  partially_refunded: { en: 'Partially refunded', ar: 'مسترد جزئياً' },
  failed: { en: 'Failed', ar: 'فشل' },
  expired: { en: 'Expired', ar: 'منتهي' },
  succeeded: { en: 'Succeeded', ar: 'ناجح' },
  pending: { en: 'Pending', ar: 'معلق' },
  open: { en: 'Open', ar: 'مفتوح' },
  approved: { en: 'Approved', ar: 'معتمد' },
  suspended: { en: 'Suspended', ar: 'موقوف' },
  rejected: { en: 'Rejected', ar: 'مرفوض' },
};
export function StatusBadge({ status, className = '' }: { status: string; className?: string }) {
  const { locale } = useApp();
  const label = statusMap[status]?.[locale] || status;
  return <span className={`badge status-${status} ${className}`}>{label}</span>;
}

// ---------- TOAST ----------
type Toast = { id: string; type: 'success' | 'error' | 'info' | 'warning'; message: string };
const toastListeners: ((t: Toast) => void)[] = [];
export function toast(message: string, type: Toast['type'] = 'info') {
  const t = { id: Math.random().toString(36).slice(2), message, type };
  toastListeners.forEach(l => l(t));
}
export function ToastStack() {
  const [items, setItems] = useState<Toast[]>([]);
  useEffect(() => {
    const handler = (t: Toast) => {
      setItems(prev => [...prev, t]);
      setTimeout(() => setItems(prev => prev.filter(x => x.id !== t.id)), 4500);
    };
    toastListeners.push(handler);
    return () => {
      const i = toastListeners.indexOf(handler);
      if (i >= 0) toastListeners.splice(i, 1);
    };
  }, []);
  if (items.length === 0) return null;
  return (
    <div className="toast-stack">
      {items.map(t => (
        <div key={t.id} className={`toast alert-${t.type}`} style={{ border: 'none', padding: '12px 16px' }}>
          <strong style={{ marginInlineEnd: 6 }}>{t.type === 'success' ? '✓' : t.type === 'error' ? '✕' : t.type === 'warning' ? '!' : 'i'}</strong>
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ---------- SKELETON ----------
export function Skeleton({ width = '100%', height = 16, radius = 6, className = '' }: { width?: number | string; height?: number | string; radius?: number; className?: string }) {
  return <div className={`skeleton ${className}`} style={{ width, height, borderRadius: radius }} />;
}

// ---------- EMPTY ----------
export function EmptyState({ icon, title, message, action }: { icon?: ReactNode; title: string; message?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-icon">{icon || <Icon.Image size={28} />}</div>
      <h3>{title}</h3>
      {message && <p>{message}</p>}
      {action}
    </div>
  );
}

// ---------- HOOK: useId (lightweight) ----------
let _idCounter = 0;
function useId(prefix: string) {
  const ref = useRef<string | null>(null);
  if (!ref.current) {
    _idCounter++;
    ref.current = `${prefix}_${_idCounter}_${Math.random().toString(36).slice(2, 6)}`;
  }
  return ref.current;
}

// ---------- KPICard ----------
export function KpiCard({ label, value, icon, trend, trendDirection, variant = 'primary' }: { label: string; value: ReactNode; icon?: ReactNode; trend?: string; trendDirection?: 'up' | 'down'; variant?: 'primary' | 'success' | 'warning' | 'danger' | 'secondary' }) {
  return (
    <div className="kpi">
      <div className={`kpi-icon ${variant !== 'primary' ? variant : ''}`}>{icon}</div>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {trend && (
        <div className={`kpi-trend ${trendDirection === 'down' ? 'down' : ''}`}>
          {trendDirection === 'down' ? <Icon.TrendingDown size={14} /> : <Icon.TrendingUp size={14} />}
          <span>{trend}</span>
        </div>
      )}
    </div>
  );
}

// ---------- BREADCRUMB ----------
export function Breadcrumb({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      {items.map((it, i) => (
        <span key={i}>
          {it.to ? <a href={it.to}>{it.label}</a> : <span>{it.label}</span>}
          {i < items.length - 1 && <span className="breadcrumb-sep">/</span>}
        </span>
      ))}
    </nav>
  );
}

// ---------- STEPPER ----------
export function Stepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div className="stepper">
      {steps.map((s, i) => (
        <span key={i} style={{ display: 'contents' }}>
          <div className={`step ${i === current ? 'active' : i < current ? 'done' : ''}`}>
            <span className="step-num">{i < current ? '✓' : i + 1}</span>
            <span>{s}</span>
          </div>
          {i < steps.length - 1 && <div className="step-connector" />}
        </span>
      ))}
    </div>
  );
}

// ---------- AVATAR ----------
export function Avatar({ name, src, size = 'md' }: { name?: string; src?: string; size?: 'sm' | 'md' | 'lg' }) {
  const initials = (name || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className={`avatar ${size !== 'md' ? `avatar-${size}` : ''}`}>
      {src ? <img src={src} alt={name || ''} /> : initials}
    </div>
  );
}

// ---------- PILL / CHIP ROW ----------
export function ChipRow({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      {label && <span style={{ fontSize: 12, color: 'var(--color-text-3)', fontWeight: 600 }}>{label}:</span>}
      {children}
    </div>
  );
}
